export interface YoutubeEmbedOptions {
  readonly additionalParams?: Readonly<Record<string, string>>
  readonly autoplay?: boolean
  readonly controls?: boolean
  readonly enableJsApi?: boolean
  readonly fullscreen?: boolean
  readonly language?: string
  readonly origin?: string
  readonly start?: number
}

const DEFAULT_YOUTUBE_LANGUAGE = 'en'
const YOUTUBE_VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/
const YOUTUBE_VIDEO_ID_TEXT_PATTERN = /(?:[?&]v=|\/embed\/|youtu\.be\/|\/shorts\/)([a-zA-Z0-9_-]{11})/

function getCurrentOrigin() {
  if (typeof window === 'undefined' || !window.location.origin) {
    return undefined
  }

  return window.location.origin
}

function getIntegerSeconds(value: number | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return undefined
  }

  return String(Math.floor(value))
}

function normalizeLegacyYoutubeText(value: string) {
  let normalizedValue = value.trim()

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const decodedValue = decodeURIComponent(normalizedValue)

      if (decodedValue === normalizedValue) {
        break
      }

      normalizedValue = decodedValue
    } catch {
      break
    }
  }

  return normalizedValue.replace(/https:\/([^/])/g, 'https://$1')
}

function getYoutubeIdFromText(value: string) {
  const match = normalizeLegacyYoutubeText(value).match(YOUTUBE_VIDEO_ID_TEXT_PATTERN)

  return normalizeYoutubeIdCandidate(match?.[1])
}

function getYoutubeIdFromUrl(url: URL) {
  const host = url.hostname.replace(/^www\./, '')
  const pathnameParts = url.pathname.split('/').filter(Boolean)

  if (host === 'youtu.be') {
    return pathnameParts[0]
  }

  if (!host.endsWith('youtube.com')) {
    return null
  }

  const watchId = url.searchParams.get('v')

  if (watchId) {
    return watchId
  }

  if (pathnameParts[0] === 'embed' || pathnameParts[0] === 'shorts') {
    return pathnameParts[1]
  }

  return null
}

function normalizeYoutubeIdCandidate(candidate: string | null | undefined) {
  const value = candidate?.trim()

  if (!value) {
    return null
  }

  const normalizedValue = value.split(/[?&#]/)[0]

  return YOUTUBE_VIDEO_ID_PATTERN.test(normalizedValue) ? normalizedValue : null
}

export function normalizeYoutubeVideoId(value: string | null | undefined) {
  const trimmedValue = value?.trim()

  if (!trimmedValue) {
    return null
  }

  const directId = normalizeYoutubeIdCandidate(trimmedValue)

  if (directId) {
    return directId
  }

  try {
    const url = new URL(normalizeLegacyYoutubeText(trimmedValue))
    return normalizeYoutubeIdCandidate(getYoutubeIdFromUrl(url)) ?? getYoutubeIdFromText(trimmedValue)
  } catch {
    return getYoutubeIdFromText(trimmedValue)
  }
}

export function getYoutubeWatchUrl(value: string | null | undefined) {
  const youtubeId = normalizeYoutubeVideoId(value)

  return youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : null
}

export function getYoutubeThumbnailUrl(value: string | null | undefined) {
  const youtubeId = normalizeYoutubeVideoId(value)

  return youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null
}

export function getYoutubeEmbedUrl(
  value: string,
  {
    additionalParams,
    autoplay = false,
    controls = true,
    enableJsApi = false,
    fullscreen = true,
    language = DEFAULT_YOUTUBE_LANGUAGE,
    origin = getCurrentOrigin(),
    start,
  }: YoutubeEmbedOptions = {},
) {
  const youtubeId = normalizeYoutubeVideoId(value)

  if (!youtubeId) {
    return null
  }

  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    cc_load_policy: '0',
    controls: controls ? '1' : '0',
    fs: fullscreen ? '1' : '0',
    hl: language,
    iv_load_policy: '3',
    modestbranding: '1',
    playsinline: '1',
    rel: '0',
  })

  if (enableJsApi) {
    params.set('enablejsapi', '1')
  }

  const normalizedStart = getIntegerSeconds(start)

  if (normalizedStart) {
    params.set('start', normalizedStart)
  }

  if (origin) {
    params.set('origin', origin)
  }

  if (additionalParams) {
    for (const [key, value] of Object.entries(additionalParams)) {
      params.set(key, value)
    }
  }

  params.set('cc_load_policy', '0')
  params.set('iv_load_policy', '3')
  params.delete('cc_lang_pref')

  return `https://www.youtube.com/embed/${youtubeId}?${params.toString()}`
}

export const disableYoutubeCaptionsCommands = [
  { args: ['captions'], event: 'command', func: 'unloadModule' },
  { args: ['cc'], event: 'command', func: 'unloadModule' },
] as const